import { useWaiterShift } from '../context/WaiterShiftContext';
import { useAuth } from '../hooks/useAuth';
import { useNotificationStateManager } from '../hooks/useNotificationStateManager';
import { triggerNotificationToggle } from './NotificationToggle';
import { useState } from 'react';

interface WaiterShiftIndicatorProps {
  isHeader?: boolean;
  onReload?: () => void;
  isRefreshing?: boolean;
}

export default function WaiterShiftIndicator({ isHeader = false, onReload, isRefreshing = false }: WaiterShiftIndicatorProps) {
  const { isWaiter, isOwner, isManager } = useAuth();
  const { isInTurn, loading, toggleShift } = useWaiterShift();
  const { isActive: notificationsActive, refreshState } = useNotificationStateManager();
  const [blocking, setBlocking] = useState(false);

  // Solo mostrar para meseros, owners y managers
  if (!isWaiter() && !isOwner() && !isManager()) {
    return null;
  }

  const handleToggle = async () => {
    try {
      // Determinar objetivo de notificaciones según próximo estado de turno
      const willBeActive = !isInTurn;
      const desiredNotifications = willBeActive; // En turno => notificaciones ON; fuera => OFF

      // Si el estado actual de notificaciones no coincide con el deseado, intentar cambiarlo y ESPERAR confirmación
      if (notificationsActive !== desiredNotifications) {
        setBlocking(true);
        // Mostrar estados "activando..." / "desactivando..." mediante disabled y spinner
        triggerNotificationToggle();

        // Esperar confirmación con reintentos
        const maxAttempts = 8;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          await new Promise(res => setTimeout(res, 750));
          try {
            await refreshState();
          } catch {}
          // Validar con estado persistido tras refresh (localStorage como respaldo)
          const stored = localStorage.getItem('notification_preferences');
          let localOk: boolean | null = null;
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (typeof parsed?.notificationsEnabled === 'boolean') {
                localOk = parsed.notificationsEnabled === desiredNotifications;
              }
            } catch {}
          }
          // Si el localStorage coincide o ya estamos en el estado deseado tras el refresh, continuamos
          if (localOk === true) break;
          if (attempt === maxAttempts) {
            setBlocking(false);
            alert(`No se pudo ${desiredNotifications ? 'activar' : 'desactivar'} las notificaciones. Intenta de nuevo.`);
            return;
          }
        }
        setBlocking(false);
      }

      // Ya confirmadas notificaciones → cambiar estado de turno
      await toggleShift();
    } catch (error: any) {
      alert(error?.message || 'No se pudo actualizar tu estado de turno');
    }
  };

  if (isHeader) {
    // Versión compacta para el header
    if (onReload) {
      // Cuando se proporciona onReload, mostrar SOLO el botón de recarga
      return (
        <button
          type="button"
          onClick={() => onReload()}
          className="flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 bg-white text-[#2C4358] hover:bg-gray-50"
          title="Refrescar"
          aria-label="Refrescar"
        >
          <span className={`${isRefreshing ? 'animate-spin' : ''} text-lg`}>↻</span>
        </button>
      );
    }
    return (
      <>
      {(blocking) && (
        <div className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-[1px] flex items-center justify-center">
          <div className="bg-white rounded-xl px-4 py-3 shadow-lg border flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#2C4358] border-t-transparent rounded-full animate-spin" />
            <div className="text-sm font-semibold text-[#2C4358]">
              {isInTurn ? 'Desactivando notificaciones...' : 'Activando notificaciones...'}
            </div>
          </div>
        </div>
      )}
      <button
        onClick={handleToggle}
        disabled={loading || blocking}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200 ${
          isInTurn
            ? 'bg-green-500/20 border-green-500/50 text-white hover:bg-green-500/30'
            : 'bg-gray-500/20 border-gray-500/50 text-white hover:bg-gray-500/30'
        } ${(loading || blocking) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        title={`${isInTurn ? 'Desactivar' : 'Activar'} turno`}
      >
        <div className={`w-2 h-2 rounded-full ${isInTurn ? 'bg-green-500' : 'bg-gray-500'}`} />
        <span className="text-xs font-bold text-white">
          {blocking ? (isInTurn ? 'DESACTIVANDO...' : 'ACTIVANDO...') : (isInTurn ? 'EN TURNO' : 'INACTIVO')}
        </span>
        {(loading || blocking) && (
          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
      </button>
      </>
    );
  }

  // Versión completa para otras ubicaciones
  return (
    <>
    {(blocking) && (
      <div className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-[1px] flex items-center justify-center">
        <div className="bg-white rounded-xl px-4 py-3 shadow-lg border flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-[#2C4358] border-t-transparent rounded-full animate-spin" />
          <div className="text-sm font-semibold text-[#2C4358]">
            {isInTurn ? 'Desactivando notificaciones...' : 'Activando notificaciones...'}
          </div>
        </div>
      </div>
    )}
    <button
      onClick={handleToggle}
      disabled={loading || blocking}
      className={`w-full rounded-xl p-4 shadow-md flex flex-col items-center transition-all border ${
        isInTurn 
          ? 'bg-green-50 border-green-200 hover:bg-green-100' 
          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
      } ${(loading || blocking) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      title="Activar/Desactivar turno"
    >
      <span className="text-3xl mb-2">
        {isInTurn ? '🟢' : '⚪️'}
      </span>
      <span className="text-base font-bold text-gray-800 text-center">
        {blocking ? (isInTurn ? 'Desactivando...' : 'Activando...') : (isInTurn ? 'En turno' : 'Fuera de turno')}
      </span>
      <span className="text-xs text-gray-500 mt-1">
        {blocking ? 'Esperando confirmación de notificaciones...' : `Tap para ${isInTurn ? 'desactivar' : 'activar'}`}
      </span>
      {(loading || blocking) && (
        <div className="mt-2 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      )}
    </button>
    </>
  );
}
